import type { SifaTeilnahme, SifaUnterweisung } from '../../services/sifa/types';
import { formatZeit, linkIstAktiv } from './unterweisungModulLogic';

/**
 * Reine Regeln der Ablage beim Kunden, Ausbau 2 (Nr. 143/144,
 * docs/sifa-ausbau2-plan.md §0 Punkte 3, 5, 6): Entwurf loeschen,
 * Sammel-Link mit QR-Code, Bestaetigung im Teilnahmendetail.
 *
 * Ohne React und ohne Supabase — die Komponenten sind nicht testbar, diese
 * Texte und Zaehlungen schon.
 */

// ─── Entwurf loeschen ───────────────────────────────────────────────────────

export interface LoeschStand {
  /** Knopf ueberhaupt anbieten: nur Entwuerfe, nie in der Nur-Ansicht. */
  zeigen: boolean;
  /** Gesetzt = Knopf gesperrt, der Text erklaert warum (Tooltip). */
  gesperrtGrund: string | null;
}

export const HAT_TEILNAHMEN_GRUND =
  'Hat Teilnahmen — nicht löschbar, bitte archivieren. Teilnahmen sind Nachweise und bleiben erhalten.';

/**
 * Darf die Liste „Löschen" anbieten? Die bekannte Zahl der Teilnahmen sperrt
 * vorab; der eigentliche Riegel sitzt im Service und in der Delete-Policy
 * (Nr. 143) — eine Teilnahme, die erst nach dem Laden entstand, faengt dort.
 */
export const entwurfLoeschStand = (
  u: Pick<SifaUnterweisung, 'status'>,
  teilnahmenAnzahl: number,
  isReadOnly: boolean,
): LoeschStand => {
  if (isReadOnly || u.status !== 'entwurf') return { zeigen: false, gesperrtGrund: null };
  return { zeigen: true, gesperrtGrund: teilnahmenAnzahl > 0 ? HAT_TEILNAHMEN_GRUND : null };
};

const titelVon = (u: Pick<SifaUnterweisung, 'titel'>): string => (u.titel || '').trim() || 'ohne Titel';

/** Rueckfrage — sagt, was mitgeht (eigene Medien) und was bleibt (Vorlagen-Medien). */
export const loeschFrage = (u: Pick<SifaUnterweisung, 'titel'>): string =>
  `Entwurf „${titelVon(u)}" löschen? Bilder und Videos dieser Unterweisung werden mit entfernt; Vorlagen-Medien bleiben unberührt.`;

/** Erfolgsmeldung; `gab = false` heisst: die Zeile war schon weg (anderer Browser). */
export const loeschMeldung = (u: Pick<SifaUnterweisung, 'nummer' | 'titel'>, gab = true): string =>
  gab
    ? `Entwurf Nr. ${u.nummer} „${titelVon(u)}" gelöscht.`
    : `Nr. ${u.nummer} „${titelVon(u)}" gibt es nicht mehr — aus der Liste genommen.`;

// ─── Sammel-Link ────────────────────────────────────────────────────────────

/**
 * Sichtbar hinter dem Sammel-Link ist, was die RPC `sifa_sammellink_public`
 * liefert: Status aktiv, Link an, nicht abgelaufen — dieselbe Regel wie
 * „Link aktiv" an der Karte (linkIstAktiv).
 */
export const zaehleSammelSichtbar = (
  unterweisungen: readonly Pick<SifaUnterweisung, 'status' | 'linkToken' | 'linkAktivBis'>[],
  jetzt: Date = new Date(),
): number => unterweisungen.filter((u) => linkIstAktiv(u, jetzt)).length;

/**
 * Hinweis zur Zahl; bei 0 als Warnung gemeint — die Seite hinter dem
 * QR-Code waere leer. null = unbekannt (Liste nicht geladen): dann keine
 * Warnung, die womoeglich gar nicht stimmt.
 */
export const sammelSichtbarText = (anzahl: number | null): string => {
  if (anzahl === null) {
    return 'Anzahl unbekannt — die Unterweisungen ließen sich nicht laden. Was über den Link erreichbar ist, entscheidet trotzdem die Datenbank.';
  }
  if (anzahl <= 0) {
    return 'Zurzeit ist keine Unterweisung freigeschaltet — die Seite hinter dem QR-Code bleibt leer, bis eine Unterweisung aktiv ist und ihren Link an hat.';
  }
  return anzahl === 1
    ? '1 Unterweisung ist über diesen Link erreichbar.'
    : `${anzahl} Unterweisungen sind über diesen Link erreichbar.`;
};

export const SAMMEL_ERKLAERUNG =
  'Ein Link für alle freigeschalteten Unterweisungen dieses Kunden — für den Aushang im Betrieb.';

/** Steht beim Schalter: AUS ist endgueltig fuer diesen Link, EIN erzeugt einen neuen. */
export const SAMMEL_SCHALTER_HINWEIS =
  'Aus macht den Link sofort ungültig. Erneutes Einschalten erzeugt einen NEUEN Link — ausgehängte QR-Codes müssen dann neu gedruckt werden.';

export const SAMMEL_AUS_FRAGE =
  'Sammel-Link ausschalten? Der Link und alle ausgehängten QR-Codes werden sofort ungültig. Beim erneuten Einschalten entsteht ein neuer Link.';

/** Vorgabe des Aushangs, vor dem Druck aenderbar (wie beim Einzel-Aushang). */
export const sammelAushangVorgabe = (): { ueberschrift: string; text: string } => ({
  ueberschrift: 'Unterweisungen',
  text: 'QR-Code scannen, Unterweisung auswählen und durchführen.',
});

/** Kunde im Dateinamen — ohne Zeichen, die ein Dateisystem stoeren. */
const dateiTeil = (kundeName: string | null | undefined): string =>
  (kundeName || '')
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 40);

export const sammelAushangDateiname = (kundeName: string | null | undefined): string => {
  const kunde = dateiTeil(kundeName);
  return `Aushang_Unterweisungen${kunde ? `_${kunde}` : ''}.pdf`;
};

export const sammelQrDateiname = (kundeName: string | null | undefined): string =>
  sammelAushangDateiname(kundeName).replace(/^Aushang_/, 'QR_').replace(/\.pdf$/, '.png');

// ─── Bestaetigung im Teilnahmendetail ───────────────────────────────────────

export type BestaetigungAnzeige =
  | { art: 'bestaetigt'; amText: string; wortlaut: string | null; datenschutzText: string }
  | { art: 'fehlt'; text: string };

/** „Datenschutzhinweise Fassung 2" bzw. „… Muster" (Fassung 0 = eingebautes Muster). */
export const datenschutzFassungText = (fassung: number | null | undefined): string => {
  if (typeof fassung !== 'number' || !Number.isFinite(fassung)) return 'Datenschutzhinweise: Fassung nicht gespeichert';
  return fassung === 0 ? 'Datenschutzhinweise: Muster' : `Datenschutzhinweise Fassung ${fassung}`;
};

/**
 * Was das Detail zur Bestaetigung zeigt. Ohne `bestaetigtAm`: eine
 * abgeschlossene Teilnahme stammt von vor Nr. 143/144, eine offene hat
 * noch gar nicht unterschrieben — das sind zwei verschiedene Aussagen.
 */
export const bestaetigungAnzeige = (
  t: Pick<SifaTeilnahme, 'bestaetigtAm' | 'bestaetigungText' | 'datenschutzFassung' | 'abgeschlossenAm'>,
): BestaetigungAnzeige => {
  if (t.bestaetigtAm) {
    return {
      art: 'bestaetigt',
      amText: `bestätigt am ${formatZeit(t.bestaetigtAm)}`,
      wortlaut: (t.bestaetigungText || '').trim() || null,
      datenschutzText: datenschutzFassungText(t.datenschutzFassung),
    };
  }
  if (!t.abgeschlossenAm) {
    return { art: 'fehlt', text: 'Noch nicht abgeschlossen — die Bestätigung folgt mit der Unterschrift.' };
  }
  return { art: 'fehlt', text: 'Abgeschlossen vor Einführung der Bestätigung — kein Bestätigungstext gespeichert.' };
};
